import { PaginationBody } from '@Entities/pagination'
import { AxiosResponse } from 'axios'
import { axiosClient } from '@App/api/api-client'
import Pathes from '@App/dicts/pathes'
import { SongwriterSummary } from '@Entities/songwriter/songwriter-summary'
import { ResultPage } from '@Entities/result-page'
import { SongwriterLink } from '@Entities/songwriter/songwriter-link'
import { WorkModel } from '@Entities/work/work'
import { SongwriterWriterSearch } from '@App/stores/search/models/songwriter-writer-search'
import { CreateSongwriterPayload } from '@Entities/songwriter/create-songwriter-payload'
import { WorkSearch } from '@App/stores/search/models/work-search'
import { WriterModelWithWorksCount } from '@Entities/work/writer-model-with-works-ount'

export const getSongwriters = (
    params: PaginationBody,
    nameFilter: string | undefined
): Promise<AxiosResponse<ResultPage<SongwriterSummary>>> =>
    axiosClient.get<ResultPage<SongwriterSummary>>(Pathes.Songwriter.list, {
        params: { page: params.page ? params.page - 1 : undefined, size: params.limit, name: nameFilter },
    })

export const getSongwriterSummary = (id: string): Promise<AxiosResponse<SongwriterSummary>> =>
    axiosClient.get<SongwriterSummary>(Pathes.Songwriter.summary(id))

export const searchSongwriterWriters = (pagination: PaginationBody, id: string, search: SongwriterWriterSearch) =>
    axiosClient.post<ResultPage<WriterModelWithWorksCount>>(Pathes.Songwriter.writerSearch(pagination, id), search)

export const searchSongwriterWriterLinks = (
    id: string,
    writerIpIds: number[]
): Promise<AxiosResponse<SongwriterLink[]>> =>
    axiosClient.post<SongwriterLink[]>(Pathes.Songwriter.writerLinkSearch(id), { affWriterIpIds: writerIpIds })

export const linkSongwriterWriter = (id: string, writerIpId: number): Promise<AxiosResponse<SongwriterLink>> =>
    axiosClient.post<SongwriterLink>(Pathes.Songwriter.linkWriter(id, writerIpId))

export const unlinkSongwriterWriter = (id: string, writerIpId: number): Promise<AxiosResponse<void>> =>
    axiosClient.delete<void>(Pathes.Songwriter.linkWriter(id, writerIpId))

export interface WriterUpdateRequestBody {
    reason: string
    writerName: string
    correctSpelling?: string
    reasonForCorrection?: string
    mergeWriterIpIds?: number[]
}

export const writerUpdateRequest = (
    id: string,
    writerIpId: number,
    body: WriterUpdateRequestBody
): Promise<AxiosResponse<void>> => axiosClient.post<void>(Pathes.Songwriter.writerUpdateRequest(id, writerIpId), body)

export const writerMergeRequest = (
    id: string,
    writerIpId: number,
    body: WriterUpdateRequestBody & { mergeWriterIpIds: number[] }
): Promise<AxiosResponse<void>> => axiosClient.post<void>(Pathes.Songwriter.writerMergeRequest(id, writerIpId), body)

export const writerIpiUpdateRequest = (id: string, writerIpId: number, newIpiNumber: string) =>
    axiosClient.post<void>(Pathes.Songwriter.writerIpiUpdateRequest(id, writerIpId), { newIpi: newIpiNumber })

export const searchSongwriterCatalog = (
    id: string,
    pagination: PaginationBody,
    search?: WorkSearch
): Promise<AxiosResponse<ResultPage<WorkModel>>> =>
    axiosClient.post<ResultPage<WorkModel>>(Pathes.Songwriter.searchSongwriterCatalog(id, pagination), search)

export const searchSongwriterCatalogSuggestions = (
    id: string,
    property: string,
    searchTerm: string,
    signal: AbortSignal
): Promise<AxiosResponse<string[]>> =>
    axiosClient.get(Pathes.Songwriter.searchSongwriterCatalogSuggestions(id, property, searchTerm), { signal })

export const createSongwriterOriginalPublisherSubmissionSuggestion = (
    id: string,
    workPropertyId: string | number,
    originalPublisherIpId: number,
    share: number,
    administratorPublisherIpId?: number
): Promise<AxiosResponse<WorkModel>> =>
    axiosClient.post<WorkModel>(
        Pathes.Songwriter.originalPublisherSubmissionSuggestion(id, workPropertyId),
        undefined,
        {
            params: {
                originalPublisherIpId,
                administratorPublisherIpId,
                share,
            },
        }
    )

export const createSongwriter = (data: CreateSongwriterPayload): Promise<AxiosResponse<SongwriterSummary>> =>
    axiosClient.post<SongwriterSummary>(Pathes.Songwriter.list, data)

export const deleteSongwriter = (id: string): Promise<AxiosResponse<void>> =>
    axiosClient.delete<void>(Pathes.Songwriter.deleteSongwriter(id))
